import React, { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import {
  ArrowRight,
  CheckCircle2,
  Loader2,
  Phone,
  RefreshCcw,
  ShieldCheck,
  User,
  Users,
  X,
  Ban,
} from 'lucide-react'
import { OtpService } from '../../services/otpService'
import { getPatient, savePatient } from '../../services/patientService'
import { authenticateDevice } from '../../services/deviceAuthService'
import { formatSriLankanMobile, getMaskedMobile, isValidSriLankanMobile } from '../../lib/utils'

const envUserId = import.meta.env.VITE_USER_ID
const instituteId = import.meta.env.VITE_INSTITUTE_ID || null
const userId = envUserId ? parseInt(envUserId, 10) : null

// CAPTCHA Generator Utility
const generateCaptcha = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'
  let text = ''
  for (let i = 0; i < 6; i++) {
    text += chars.charAt(Math.floor(Math.random() * chars.length))
  }

  const canvas = document.createElement('canvas')
  canvas.width = 200
  canvas.height = 80
  const ctx = canvas.getContext('2d')

  // Transparent background
  ctx.clearRect(0, 0, canvas.width, canvas.height)

  // Color palette for letters
  const colors = ['#1e293b', '#dc2626', '#2563eb', '#059669', '#7c3aed', '#ea580c', '#0891b2', '#d97706']

  // Draw text
  ctx.font = 'bold 40px Arial'
  ctx.textBaseline = 'middle'

  for (let i = 0; i < text.length; i++) {
    ctx.save()
    const x = 20 + i * 30
    const y = 40
    const angle = (Math.random() - 0.5) * 0.4
    ctx.translate(x, y)
    ctx.rotate(angle)
    ctx.shadowColor = 'rgba(0,0,0,0.1)'
    ctx.shadowBlur = 3
    ctx.fillStyle = colors[Math.floor(Math.random() * colors.length)]
    ctx.fillText(text[i], 0, 0)
    ctx.restore()
  }

  return {
    text,
    image: canvas.toDataURL('image/png'),
  }
}

const Message = ({ tone = 'info', children }) => {
  const styles = {
    info: 'bg-slate-50 text-slate-700 border-slate-200',
    success: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    error: 'bg-rose-50 text-rose-700 border-rose-200',
    warning: 'bg-amber-50 text-amber-700 border-amber-200',
  }

  return (
    <div className={`rounded-xl border px-3 py-2 text-sm ${styles[tone]}`}>{children}</div>
  )
}

const EmptyState = ({ title, body, actionLabel, onAction }) => (
  <div className="w-full rounded-xl border border-dashed border-slate-200 bg-slate-50/60 p-6 text-center">
    <p className="text-base font-semibold text-slate-800">{title}</p>
    <p className="mt-2 text-sm text-slate-600">{body}</p>
    {actionLabel && (
      <button
        onClick={onAction}
        className="mt-4 inline-flex items-center justify-center rounded-full bg-slate-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-slate-800"
      >
        {actionLabel}
      </button>
    )}
  </div>
)

function LoginModal({ open, defaultMode = 'login', onClose, onJoinUs, onSuccess, onOpenSignup }) {
  const { t } = useTranslation()
  const [mode, setMode] = useState(defaultMode) // 'login' | 'signup'
  const [step, setStep] = useState(1) // 1: mobile+captcha, 2: otp, 3: select patient
  const [form, setForm] = useState({ mobile: '' })
  const [captchaImage, setCaptchaImage] = useState('')
  const [captchaText, setCaptchaText] = useState('')
  const [captchaValue, setCaptchaValue] = useState('')
  const [otp, setOtp] = useState('')
  const [otpSent, setOtpSent] = useState(false)
  const [otpVerified, setOtpVerified] = useState(false)
  const [otpToken, setOtpToken] = useState('')
  const [patients, setPatients] = useState([])
  const [selectedPatient, setSelectedPatient] = useState(null)
  const [statusMessage, setStatusMessage] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [showPatientModal, setShowPatientModal] = useState(false)
  const [signupForm, setSignupForm] = useState({
    title: '',
    firstName: '',
    lastName: '',
    nic: '',
    dateOfBirth: '',
    gender: '',
    bloodGroup: '',
    address: '',
    occupation: '',
    maritalStatus: '',
  })

  const formattedMobile = useMemo(() => formatSriLankanMobile(form.mobile), [form.mobile])
  const maskedMobile = useMemo(() => getMaskedMobile(form.mobile), [form.mobile])

  useEffect(() => {
    if (open) {
      resetState(defaultMode)
      loadInitialCaptcha()
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'auto'
    }

    return () => {
      document.body.style.overflow = 'auto'
    }
  }, [open, defaultMode])

  const resetState = (nextMode = 'login') => {
    setMode(nextMode)
    setStep(1)
    setForm({ mobile: '' })
    setCaptchaImage('')
    setCaptchaText('')
    setCaptchaValue('')
    setOtp('')
    setOtpSent(false)
    setOtpVerified(false)
    setPatients([])
    setSelectedPatient(null)
    setStatusMessage('')
    setErrorMessage('')
    setLoading(false)
    setSaving(false)
    setSignupForm({
      title: '',
      firstName: '',
      lastName: '',
      nic: '',
      dateOfBirth: '',
      gender: '',
      bloodGroup: '',
      address: '',
      occupation: '',
      maritalStatus: '',
    })
  }

  if (!open || typeof document === 'undefined') return null

  const loadInitialCaptcha = async () => {
    try {
      const { text, image } = generateCaptcha()
      setCaptchaImage(image)
      setCaptchaText(text)
      setCaptchaValue('')
    } catch (error) {
      console.error('Initial CAPTCHA generation failed:', error)
    }
  }

  const loadCaptcha = async () => {
    setLoading(true)
    setErrorMessage('')
    setStatusMessage(t('login_modal_status_generating_captcha'))

    try {
      const { text, image } = generateCaptcha()
      setCaptchaImage(image)
      setCaptchaText(text)
      setCaptchaValue('')
      setStatusMessage(t('login_modal_status_enter_captcha'))
    } catch (error) {
      console.error('CAPTCHA generation failed:', error)
      setErrorMessage(t('login_modal_error_captcha_gen_failed'))
    }

    setLoading(false)
  }

  const refreshCaptcha = async (clearError = true) => {
    setLoading(true)
    try {
      const { text, image } = generateCaptcha()
      setCaptchaImage(image)
      setCaptchaText(text)
      setCaptchaValue('')
      if (clearError) setErrorMessage('')
    } catch (error) {
      console.error('CAPTCHA refresh failed:', error)
      setErrorMessage(t('login_modal_error_captcha_refresh_failed'))
    }
    setLoading(false)
  }

  const verifyCaptchaAndSendOtp = async () => {
    if (!captchaValue.trim()) {
      setErrorMessage(t('login_modal_error_enter_captcha'))
      return
    }

    if (!isValidSriLankanMobile(form.mobile)) {
      setErrorMessage(t('login_modal_error_valid_mobile'))
      return
    }

    // Frontend CAPTCHA verification
    if (captchaValue.trim().toUpperCase() !== captchaText.toUpperCase()) {
      setErrorMessage(t('login_modal_error_captcha_failed'))
      await refreshCaptcha(false)
      return
    }

    setLoading(true)
    setErrorMessage('')
    setStatusMessage(t('login_modal_status_captcha_verified'))
    const sent = await sendOtp()
    if (sent) {
      setStatusMessage(t('login_modal_status_otp_sent'))
      setStep(2) // Move to OTP step
    }
    setLoading(false)
  }

  const sendOtp = async () => {
    const response = await OtpService.sendOtp(formattedMobile, instituteId)
    if (response.success) {
      setOtp('')
      setOtpSent(true)
      setStatusMessage(t('login_modal_status_otp_sent'))
      setErrorMessage('')
      return true
    }

    setErrorMessage(t('login_modal_error_otp_failed'))
    return false
  }

  const verifyOtp = async () => {
    if (!otp || otp.length < 4) {
      setErrorMessage(t('login_modal_error_enter_otp'))
      return
    }

    setLoading(true)
    setErrorMessage('')
    setStatusMessage(t('login_modal_status_verifying_otp'))

    const response = await OtpService.verifyOtp(formattedMobile, otp)

    if (!response.success) {
      setLoading(false)
      setErrorMessage(t('login_modal_error_otp_invalid'))
      return
    }

    if (response.data?.token) {
      setOtpToken(response.data.token)
    }

    setOtpVerified(true)
    setStatusMessage(t('login_modal_verified_success'))
    if (mode === 'login') {
      await fetchPatients(response.data?.token)
      setStep(3) // Move to patient selection step
    }
    setLoading(false)
  }

  const fetchPatients = async (token) => {
    if (!userId) {
      setStatusMessage('OTP verified, but missing user configuration (VITE_USER_ID).')
      setPatients([])
      return
    }

    try {
      setStatusMessage(t('login_modal_status_looking_records'))
      const response = await getPatient({
        UserId: userId,
        Mobile: formattedMobile,
        DateOfBirth: '',
        UniqueId: '',
        token: token,
        isDeviceToken: false  // This is called with OTP token during login
      })

      if (response.status === 1000 && Array.isArray(response.data)) {
        setPatients(response.data)
        if (response.data.length === 1) {
          handleSelectPatient(response.data[0])
        }
      } else {
        setPatients([])
        setErrorMessage(t('login_modal_error_no_records_found'))
      }
    } catch (error) {
      console.error('Patient lookup failed:', error)
      setPatients([])
      setErrorMessage(t('login_modal_error_fetch_records_failed'))
    }
  }

  const handleSelectPatient = async (patient) => {
    try {
      setLoading(true)
      setErrorMessage('')
      setStatusMessage(t('login_modal_status_authenticating_device'))
      
      // Authenticate device and get patient-specific token
      const deviceAuthResponse = await authenticateDevice({
        PatientId: patient.Id
      }, otpToken)
      
      if (deviceAuthResponse.status === 1000 && deviceAuthResponse.data?.token) {
        setSelectedPatient(patient)
        setStatusMessage(`Signed in as ${patient.Title || ''} ${patient.FirstName || ''} ${patient.LastName || ''}`.trim())
        
        // Pass both patient data and device token to parent
        onSuccess?.({
          ...patient,
          deviceToken: deviceAuthResponse.data.token,
          refreshToken: deviceAuthResponse.data.refreshToken
        })
      } else {
        setErrorMessage(t('login_modal_error_auth_device_failed'))
      }
    } catch (error) {
      console.error('Device authentication failed:', error)
      setErrorMessage(t('login_modal_error_generic'))
    } finally {
      setLoading(false)
    }
  }

  const goToJoinUs = () => {
    if (onOpenSignup) {
      // Close login modal and open signup modal with verified mobile
      onClose?.()
      onOpenSignup(formattedMobile)
    } else {
      // Fallback to old behavior
      setMode('signup')
      setStatusMessage('Create your patient profile to continue')
      setErrorMessage('')
    }
  }

  const renderSignupForm = () => (
    <div className="space-y-3">
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        <div>
          <label className="text-xs font-semibold text-slate-700">Title</label>
          <select
            value={signupForm.title}
            onChange={(e) => setSignupForm((p) => ({ ...p, title: e.target.value }))}
            className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-200"
          >
            <option value="">Select</option>
            <option value="Mr.">Mr.</option>
            <option value="Mrs.">Mrs.</option>
            <option value="Ms.">Ms.</option>
            <option value="Dr.">Dr.</option>
          </select>
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-700">First Name</label>
          <input
            value={signupForm.firstName}
            onChange={(e) => setSignupForm((p) => ({ ...p, firstName: e.target.value }))}
            className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-200"
            placeholder="John"
          />
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-700">Last Name</label>
          <input
            value={signupForm.lastName}
            onChange={(e) => setSignupForm((p) => ({ ...p, lastName: e.target.value }))}
            className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-200"
            placeholder="Doe"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        <div>
          <label className="text-xs font-semibold text-slate-700">NIC</label>
          <input
            value={signupForm.nic}
            onChange={(e) => setSignupForm((p) => ({ ...p, nic: e.target.value }))}
            className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-200"
            placeholder="123456789V"
          />
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-700">Date of Birth</label>
          <input
            type="date"
            value={signupForm.dateOfBirth}
            onChange={(e) => setSignupForm((p) => ({ ...p, dateOfBirth: e.target.value }))}
            className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-200"
          />
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-700">Gender</label>
          <select
            value={signupForm.gender}
            onChange={(e) => setSignupForm((p) => ({ ...p, gender: e.target.value }))}
            className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-200"
          >
            <option value="">Select</option>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
            <option value="Other">Other</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        <div>
          <label className="text-xs font-semibold text-slate-700">Blood Group</label>
          <select
            value={signupForm.bloodGroup}
            onChange={(e) => setSignupForm((p) => ({ ...p, bloodGroup: e.target.value }))}
            className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-200"
          >
            <option value="">Select</option>
            <option value="O+">O+</option>
            <option value="O-">O-</option>
            <option value="A+">A+</option>
            <option value="A-">A-</option>
            <option value="B+">B+</option>
            <option value="B-">B-</option>
            <option value="AB+">AB+</option>
            <option value="AB-">AB-</option>
          </select>
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-700">Occupation</label>
          <input
            value={signupForm.occupation}
            onChange={(e) => setSignupForm((p) => ({ ...p, occupation: e.target.value }))}
            className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-200"
            placeholder="e.g., Student"
          />
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-700">Marital Status</label>
          <select
            value={signupForm.maritalStatus}
            onChange={(e) => setSignupForm((p) => ({ ...p, maritalStatus: e.target.value }))}
            className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-200"
          >
            <option value="">Select</option>
            <option value="1">Single</option>
            <option value="2">Married</option>
            <option value="3">Divorced</option>
            <option value="4">Widowed</option>
          </select>
        </div>
      </div>

      <div>
        <label className="text-xs font-semibold text-slate-700">Address</label>
        <textarea
          value={signupForm.address}
          onChange={(e) => setSignupForm((p) => ({ ...p, address: e.target.value }))}
          className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-200"
          rows={2}
          placeholder="Enter your address"
        />
      </div>
    </div>
  )

  const handleSignup = async () => {
    if (!otpVerified) {
      setErrorMessage('Please verify the OTP before signing up.')
      return
    }
    if (!userId) {
      setErrorMessage('Missing user configuration (VITE_USER_ID).')
      return
    }
    if (!signupForm.firstName || !signupForm.lastName || !signupForm.dateOfBirth || !signupForm.gender || !signupForm.title) {
      setErrorMessage('Please fill all required fields.')
      return
    }

    setSaving(true)
    setErrorMessage('')
    setStatusMessage('Creating your profile...')

    try {
      const response = await savePatient({
        UserId: userId,
        Title: signupForm.title,
        FirstName: signupForm.firstName,
        LastName: signupForm.lastName,
        Mobile: formattedMobile,
        Gender: signupForm.gender,
        DateOfBirth: signupForm.dateOfBirth,
        NIC: signupForm.nic,
        BloodGroup: signupForm.bloodGroup,
        Occupation: signupForm.occupation,
        MaritalStatus: signupForm.maritalStatus,
        Address: signupForm.address,
      })

      if (response.success) {
        setStatusMessage('Profile created successfully. Authenticating device...')
        setSelectedPatient(response.data)
        
        // Authenticate device after creating patient
        try {
          const deviceAuthResponse = await authenticateDevice({
            PatientId: response.data.Id
          }, otpToken)
          
          if (deviceAuthResponse.status === 1000 && deviceAuthResponse.data?.token) {
            setStatusMessage('Profile created and authenticated successfully.')
            // Pass both patient data and device token to parent
            onSuccess?.({
              ...response.data,
              deviceToken: deviceAuthResponse.data.token,
              refreshToken: deviceAuthResponse.data.refreshToken
            })
          } else {
            setErrorMessage('Profile created but authentication failed.')
            onSuccess?.(response.data)
          }
        } catch (authError) {
          console.error('Device authentication failed:', authError)
          setErrorMessage('Profile created but authentication failed.')
          onSuccess?.(response.data)
        }
      } else {
        setErrorMessage(response.message || 'Could not create profile. Please try again.')
      }
    } catch (error) {
      console.error('Signup failed', error)
      setErrorMessage('Could not create profile. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  const renderPatientPicker = () => (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-semibold text-slate-900">Select your patient record</p>
          <p className="text-xs text-slate-500">{patients.length ? `${patients.length} profile(s) found for this mobile` : 'No matching profiles found.'}</p>
        </div>
      </div>

      {patients.length ? (
        <div className="grid gap-3 sm:grid-cols-2">
          {patients.map((patient) => (
            <button
              key={patient.Id}
              onClick={() => handleSelectPatient(patient)}
              className="group w-full rounded-xl border border-slate-200 bg-white p-4 text-left transition hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-lg"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-slate-700">
                    <User size={18} />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-900">
                      {[patient.Title, patient.FirstName, patient.LastName].filter(Boolean).join(' ')}
                    </p>
                    <p className="text-xs text-slate-500">{patient.NIC || 'NIC not provided'}</p>
                  </div>
                </div>
                <CheckCircle2 className="text-slate-300 transition group-hover:text-slate-900" size={20} />
              </div>
              {patient.DateOfBirth && (
                <p className="mt-1 text-xs text-slate-500">DOB: {new Date(patient.DateOfBirth).toLocaleDateString('en-GB')}</p>
              )}
            </button>
          ))}
        </div>
      ) : (
        <EmptyState
          title="No patient profiles found"
          body="Create a new patient profile to continue."
          actionLabel="Create New Profile"
          onAction={goToJoinUs}
        />
      )}
    </div>
  )

  const otpVerifiedPill = otpVerified ? (
    <span className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
      <ShieldCheck size={14} /> OTP Verified
    </span>
  ) : null

  const gradientBg = 'bg-gradient-to-r from-[#7096F3] via-[#4181F3] via-40% to-[#C08EDB]'

  return createPortal(
    <div
      className="fixed inset-0 z-[9995] flex items-center justify-center bg-slate-950/70 px-3 py-3 backdrop-blur-md"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.()
      }}
    >
      <div className="relative w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-black/5">

        <header className={`relative z-10 flex items-center justify-between px-5 py-2 text-white ${gradientBg}`}>
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-white/80 -mb-0.5">Medica</p>
            <p className="text-xl font-bold">
              {step === 1 && t('login_modal_step_login')}
              {step === 2 && t('login_modal_step_verify_otp')}
              {step === 3 && t('login_modal_step_select_profile')}
            </p>
          </div>
          <button
            onClick={onClose}
            className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/20 text-white hover:bg-white/30"
            aria-label={t('login_modal_close_aria')}
          >
            <X size={18} />
          </button>
        </header>

        <div className="relative z-10 px-5 pb-6 pt-5 min-h-[380px] overflow-y-auto">
          {/* Step 1: Mobile Number + CAPTCHA */}
          {step === 1 && (
            <div className="space-y-3 -mt-1">
              <p className="text-md font-bold text-slate-900 -mb-3">{t('login_modal_mobile_verif_title')}</p>
              <p className="text-xs text-slate-500">{t('login_modal_mobile_verif_desc')}</p>

              <div className="mt-3 space-y-3">
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                  <input
                    value={form.mobile}
                    onChange={(e) => setForm({ mobile: e.target.value })}
                    className="w-full rounded-lg border border-slate-200 bg-white px-10 py-3 text-sm font-semibold text-slate-900 outline-none transition focus:border-slate-400 focus:ring-2 focus:ring-slate-200"
                    placeholder={t('login_modal_mobile_placeholder')}
                    inputMode="numeric"
                    maxLength={12}
                  />
                  {form.mobile && (
                    <span className={`absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold ${isValidSriLankanMobile(form.mobile) ? 'text-green-600' : 'text-red-600'}`}>
                      {isValidSriLankanMobile(form.mobile) ? t('login_modal_mobile_valid') : t('login_modal_mobile_invalid')}
                    </span>
                  )}
                </div>

                <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3 flex-1 flex flex-col">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-bold text-slate-800">{t('login_modal_captcha_title')}</p>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={refreshCaptcha}
                        className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-white"
                      >
                        <RefreshCcw size={12} className={loading ? 'animate-spin' : ''} />
                        {t('login_modal_captcha_refresh')}
                      </button>
                    </div>
                  </div>

                  <div className="mt-2 flex-1 flex flex-col justify-between">
                    <div className="overflow-hidden rounded-lg border-slate-200 bg-white flex-1 flex items-center justify-center">
                      {captchaImage ? (
                        <img src={captchaImage} alt="Captcha" className="max-h-full p-6 w-full object-contain" />
                      ) : (
                        <div className="text-xs text-slate-500">{t('login_modal_captcha_placeholder')}</div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-3">
                      <input
                        value={captchaValue}
                        onChange={(e) => setCaptchaValue(e.target.value)}
                        className="flex-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-200"
                        placeholder={t('login_modal_captcha_input_placeholder')}
                      />
                      <div className="relative group">
                        <button
                          onClick={verifyCaptchaAndSendOtp}
                          disabled={loading || !captchaValue || captchaValue.length !== 6 || !isValidSriLankanMobile(form.mobile) || otpSent}
                          className={`relative rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-sm transition ${gradientBg} disabled:opacity-60`}
                        >
                          {loading ? <Loader2 className="animate-spin" size={14} /> : t('login_modal_send_otp')}
                        </button>
                        {(loading || !captchaValue || captchaValue.length !== 6 || !isValidSriLankanMobile(form.mobile)) && (
                          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/20 rounded-lg">
                            <Ban size={16} className="text-white" />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {errorMessage && (
                <div className="mt-4">
                  <Message tone="error">{errorMessage}</Message>
                </div>
              )}
            </div>
          )}

          {/* Step 2: OTP Verification */}
          {step === 2 && (
            <div className="space-y-3">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-900">
                  {t('login_modal_otp_sent_info', { mobile: maskedMobile })}
                </p>
              </div>

              <div className="rounded-lg border border-slate-200 bg-white p-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="text-sm font-semibold text-slate-800">{t('login_modal_otp_enter_title')}</p>
                    <p className="text-xs text-slate-500">{t('login_modal_otp_enter_desc')}</p>
                  </div>
                  <button
                    onClick={sendOtp}
                    className="text-xs font-semibold text-slate-700 underline-offset-4 hover:underline"
                  >
                    {t('login_modal_otp_resend')}
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    value={otp}
                    onChange={(e) => {
                      const value = e.target.value
                      if (/^\d*$/.test(value)) setOtp(value)
                    }}
                    className="flex-1 rounded-lg border border-slate-200 bg-white px-3 py-3 text-center text-lg font-semibold tracking-[0.3em] text-slate-900 outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-200"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="••••••"
                    autoFocus
                  />
                  <button
                    onClick={verifyOtp}
                    disabled={loading || !otp}
                    className={`rounded-lg px-6 py-3 text-sm font-semibold text-white shadow-sm transition ${gradientBg} disabled:opacity-60`}
                  >
                    {loading ? <Loader2 className="animate-spin" size={14} /> : t('login_modal_otp_verify')}
                  </button>
                </div>
              </div>

              <button
                onClick={() => setStep(1)}
                className="w-full text-center text-sm text-slate-600 hover:text-slate-900 underline-offset-4 hover:underline"
              >
                {t('login_modal_otp_change_number')}
              </button>

              {errorMessage && (
                <div className="mt-4">
                  <Message tone="error">{errorMessage}</Message>
                </div>
              )}
            </div>
          )}

          {/* Step 3: Patient Selection */}
          {step === 3 && (
            <div className="space-y-3">
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                <div className="flex items-center gap-2">
                  <ShieldCheck size={16} className="text-emerald-600" />
                  <p className="text-sm text-emerald-900 font-medium">
                    {t('login_modal_verified_success')}
                  </p>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="text-sm font-semibold text-slate-900">{t('login_modal_select_profile_title')}</p>
                    <p className="text-xs text-slate-500">
                      {patients.length
                        ? (patients.length === 1
                            ? t('login_modal_profiles_found_one', { mobile: maskedMobile })
                            : t('login_modal_profiles_found_other', { count: patients.length, mobile: maskedMobile }))
                        : t('login_modal_no_profiles_found')}
                    </p>
                  </div>
                </div>

                {patients.length > 0 ? (
                  <div className="space-y-2 max-h-36 overflow-y-auto">
                    {patients.map((patient) => (
                      <button
                        key={patient.Id}
                        onClick={() => handleSelectPatient(patient)}
                        className="w-full text-left p-3 border border-slate-200 rounded-lg hover:bg-blue-50 hover:border-blue-300 transition-colors"
                      >
                        <p className="font-medium text-slate-900">
                          {patient.Title} {patient.FirstName} {patient.LastName}
                        </p>
                        <p className="text-xs text-slate-500 mt-1">
                          {t('login_modal_nic_label')}{patient.NIC || t('login_modal_not_available')} | {t('login_modal_dob_label')}{patient.DateOfBirth ? new Date(patient.DateOfBirth).toLocaleDateString('en-GB') : t('login_modal_not_available')}
                        </p>
                        {patient.UniqueId && (
                          <p className="text-xs text-slate-500">{t('login_modal_id_label')}{patient.UniqueId}</p>
                        )}
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center p-6 text-center border border-dashed border-slate-200 rounded-lg bg-slate-50">
                    <p className="text-sm font-semibold text-slate-900">{t('login_modal_no_profiles_found')}</p>
                    <p className="text-xs text-slate-500 mt-1">{t('login_modal_no_profiles_sub')}</p>
                  </div>
                )}

                <div className="pt-3 border-t border-slate-200 mt-3">
                  <button
                    onClick={goToJoinUs}
                    className="w-full text-center p-3 border-2 border-dashed border-blue-300 rounded-lg hover:bg-blue-50 transition-colors text-blue-600 font-medium"
                  >
                    {t('login_modal_create_profile_btn')}
                  </button>
                </div>
              </div>

              {errorMessage && (
                <div className="mt-4">
                  <Message tone="error">{errorMessage}</Message>
                </div>
              )}
            </div>
          )}
        </div>

      </div>
    </div>,
    document.body
  )
}

export default LoginModal
